// apps/client/src/hooks/useMonthlyAggregates.ts
// MIGRATED (2025-11-26): Now uses BigQuery via getMonthlyCommissions Cloud Function
// ENHANCED (2025-11-26): Added byBroker and byLender breakdowns for dashboard filtering
// MIGRATED (2026-07-10): Fetches through the shared dashboard query cache
//   (useDashboardQuery). Return shape and callable payload are unchanged;
//   caching semantics are per-tenant via featureFlags.dashboard_query_cache.
// Previous: Queried Firestore companies/{companyId}/monthly_aggregates collection (deprecated)
// Current: Queries BigQuery raw_commissions table via Cloud Function

import { useMemo } from 'react';
import { MonthlyAggregate } from '../types';
import { useDashboardQuery } from './useDashboardQuery';

interface UseMonthlyAggregatesParams {
  companyId: string;
  // Option A: Year-based (existing, backward compatible)
  year?: number;
  yearType?: 'calendar' | 'financial';
  // Option B: Month-range based (new - for relative periods)
  startMonth?: string;  // YYYY-MM format
  endMonth?: string;    // YYYY-MM format
  // Filters
  officeId?: string;
  templateId?: string;
  entityCode?: string;
}

interface UseMonthlyAggregatesReturn {
  aggregates: MonthlyAggregate[];
  loading: boolean;
  error: string | null;
  upfrontCommission: number;
  trailCommission: number;
  templateIds: string[];  // ✅ FIX: Template IDs with data for this office
}

// ============================================================================
// ENHANCED RESPONSE TYPE (2025-11-26)
// Added byBroker and byLender breakdowns to match getMonthlyCommissions.ts
// ============================================================================
interface MonthlyCommissionsResponse {
  months: Array<{
    month: string;           // 'YYYY-MM'
    upfrontNetCents: number;
    trailNetCents: number;
    byBroker?: Record<string, {
      brokerDisplay: string;
      upfrontNetCents: number;
      trailNetCents: number;
    }>;
    byLender?: Record<string, {
      upfrontNetCents: number;
      trailNetCents: number;
      totalLoanAmountCents: number;  // ENHANCED (2025-12-13): For TopLendersWidget Volume metric
      lineOfTrail: number;           // ENHANCED (2025-12-13): For TopLendersWidget Portfolio metric
    }>;
  }>;
  totals: {
    upfrontCommission: number;  // Sum in cents
    trailCommission: number;    // Sum in cents
  };
  templateIds: string[];  // ✅ FIX: Template IDs with data for this office
}

/**
 * Hook to fetch monthly commission aggregates (FLOW metrics - cumulative)
 * Used for: Upfront Commission, Trail Commission totals and charts
 *
 * MIGRATED (2025-11-26): Now uses BigQuery via getMonthlyCommissions Cloud Function
 * ENHANCED (2025-11-26): Includes byBroker and byLender for dashboard filtering
 * Previous: Queried Firestore monthly_aggregates collection (deprecated, no longer populated)
 */
export function useMonthlyAggregates({
  companyId,
  year,
  yearType,
  startMonth,
  endMonth,
  officeId,
  templateId,
  entityCode,
}: UseMonthlyAggregatesParams): UseMonthlyAggregatesReturn {
  const { data, loading, error } = useDashboardQuery<MonthlyCommissionsResponse>(
    'getMonthlyCommissions',
    {
      companyId,
      ...(startMonth && endMonth
        ? { startMonth, endMonth }
        : { year, yearType }),
      officeId,
      templateId,
      entityCode,
    }
  );

  const templateIds = useMemo(() => data?.templateIds || [], [data]);

  // Transform Cloud Function response to match existing MonthlyAggregate interface
  // Backend returns 'YYYY-MM' format, we parse to year/month numbers
  const aggregates = useMemo<MonthlyAggregate[]>(() => {
    if (!data) return [];

    return data.months.map((item) => {
      // Parse 'YYYY-MM' format
      const [yearStr, monthStr] = item.month.split('-');

      // ============================================================================
      // TRANSFORM byBroker (2025-11-26)
      // Map from backend format to MonthlyAggregate.byBroker format
      // ============================================================================
      const byBroker = item.byBroker
        ? Object.fromEntries(
            Object.entries(item.byBroker).map(([brokerId, brokerData]) => [
              brokerId,
              {
                brokerDisplay: brokerData.brokerDisplay,
                brokerId,
                upfrontGross: brokerData.upfrontNetCents,
                upfrontAdjustments: 0,
                upfrontNet: brokerData.upfrontNetCents,
                trailGross: brokerData.trailNetCents,
                trailAdjustments: 0,
                trailNet: brokerData.trailNetCents,
                totalLoanAmount: 0,
                lineOfTrail: 0,
                recordCount: 0,
              },
            ])
          )
        : undefined;

      // ============================================================================
      // TRANSFORM byLender (2025-11-26)
      // Map from backend format to MonthlyAggregate.byLender format
      // ENHANCED (2025-12-13): Now includes totalLoanAmount and lineOfTrail from backend
      // ============================================================================
      const byLender = item.byLender
        ? Object.fromEntries(
            Object.entries(item.byLender).map(([lender, lenderData]) => [
              lender,
              {
                upfrontGross: lenderData.upfrontNetCents,
                upfrontAdjustments: 0,
                upfrontNet: lenderData.upfrontNetCents,
                trailGross: lenderData.trailNetCents,
                trailAdjustments: 0,
                trailNet: lenderData.trailNetCents,
                totalLoanAmount: lenderData.totalLoanAmountCents || 0,  // Now from backend
                lineOfTrail: lenderData.lineOfTrail || 0,               // Now from backend
                recordCount: 0,
              },
            ])
          )
        : undefined;

      return {
        yearMonth: item.month,
        year: parseInt(yearStr, 10),
        month: parseInt(monthStr, 10),
        upfrontCommission: item.upfrontNetCents,  // Already in cents
        trailCommission: item.trailNetCents,      // Already in cents
        totalLoanAmount: 0,  // Not returned by this endpoint (use getRolling3Run for loan book)
        recordCount: 0,      // Not returned by this endpoint
        byBroker,
        byLender,
      };
    });
  }, [data]);

  // Calculate totals from aggregates
  const upfrontCommission = aggregates.reduce((sum, agg) => sum + (agg.upfrontCommission || 0), 0);
  const trailCommission = aggregates.reduce((sum, agg) => sum + (agg.trailCommission || 0), 0);

  return {
    aggregates,
    loading,
    error,
    upfrontCommission,
    trailCommission,
    templateIds,  // ✅ FIX: Return template IDs for tab visibility
  };
}
